import { cn } from "@/lib/utils";
import { StatusBadge, PaymentBadge, PayoutBadge, ConfirmPaymentButton } from "./TherapyBadges";
import { TherapyCard } from "./TherapyCard";
import { formatBookedAt, formatDateShort, formatTime, getInitials, sessionShares } from "./helpers";
import { Pagination } from "@/components/ui/pagination";
import { flagEmoji } from "@/lib/flag-emoji";
import { computeAdminPaymentBreakdown, formatMoney } from "@/lib/admin-payment-breakdown";
import type { Therapy } from "@/types";

interface TherapiesTableProps {
  therapies: Therapy[];
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function PersonAvatar({
  name,
  tone,
  className,
}: {
  name: string;
  tone: "muted" | "primary";
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex size-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
        tone === "primary" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
        className,
      )}
    >
      {getInitials(name)}
    </div>
  );
}

function Th({
  children,
  align = "left",
  className,
}: {
  children: React.ReactNode;
  align?: "left" | "right" | "center";
  className?: string;
}) {
  return (
    <th
      className={cn(
        "px-4 py-3 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground",
        align === "right" && "text-right",
        align === "center" && "text-center",
        align === "left" && "text-left",
        className,
      )}
    >
      {children}
    </th>
  );
}

/**
 * Tooltip text with the full accounting breakdown for the client price cell.
 * Kod terapeuta obveznika PDV-a "Zarada" (`therapistEarnings`) sadrži i njegov ulazni
 * PDV, pa bez zasebne stavke Neto != Platforma + Zarada i čitalac vidi račun koji ne
 * štima. Ta linija se prikazuje samo kad ulazni PDV zaista postoji.
 */
function breakdownTitle(
  b: ReturnType<typeof computeAdminPaymentBreakdown>,
  shares: ReturnType<typeof sessionShares>,
): string {
  const parts = [
    // Kod sufinansiranja ćelija pokazuje dio klijenta, pa ukupna cijena i dio
    // firme idu u tooltip da obračun (Neto + PDV) i dalje ima od čega krenuti.
    ...(shares.cofunded
      ? [`Ukupno ${formatMoney(b.clientPaid)} KM`, `Firma ${formatMoney(shares.companyPays)} KM`]
      : []),
    `Neto ${formatMoney(b.netBeforeVat)} KM`,
    `PDV ${formatMoney(b.vat)} KM`,
    `Platforma ${formatMoney(b.platformFee)} KM`,
  ];
  if (b.therapistVat !== null && b.therapistVat > 0) {
    parts.push(`PDV terapeuta ${formatMoney(b.therapistVat)} KM`);
  }
  return parts.join("  ·  ");
}

export function TherapiesTable({
  therapies,
  page,
  totalPages,
  onPageChange,
}: TherapiesTableProps) {
  return (
    <>
      {/* Desktop table */}
      <div className="hidden lg:block">
        <div className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
          <table className="w-full text-sm">
            <colgroup>
              <col className="w-[130px]" />
              <col />
              <col />
              <col className="w-[120px]" />
              <col className="w-[100px]" />
              <col className="w-[110px]" />
              <col className="w-[130px]" />
              <col className="w-[100px]" />
            </colgroup>
            <thead>
              <tr className="border-b border-border/60 bg-muted/30">
                <Th>Sesija</Th>
                <Th>Klijent</Th>
                <Th>Terapeut</Th>
                <Th align="right">Klijent plaća</Th>
                <Th align="right">Zarada</Th>
                <Th>Status</Th>
                <Th>Plaćanje</Th>
                <Th>Isplata</Th>
              </tr>
            </thead>
            <tbody>
              {therapies.map((t, i) => {
                const breakdown = computeAdminPaymentBreakdown(t);
                const shares = sessionShares(t);

                return (
                  <tr
                    key={t.id}
                    className={cn(
                      "group transition-colors",
                      t.paymentStatus === "pending_transfer"
                        ? "bg-amber-50/60 hover:bg-amber-50"
                        : "hover:bg-primary/[0.03]",
                      i !== therapies.length - 1 && "border-b border-border/40"
                    )}
                  >
                    <td className="px-4 py-3.5">
                      <div className="whitespace-nowrap font-medium text-foreground leading-tight">
                        {formatDateShort(t.scheduledAt)}
                      </div>
                      <div className="mt-1 whitespace-nowrap text-xs text-muted-foreground">
                        {formatTime(t.scheduledAt)} · {t.durationMinutes} min
                      </div>
                      {t.createdAt && (
                        <div className="mt-1 whitespace-nowrap text-[11px] text-muted-foreground/70">
                          Rezervisano {formatBookedAt(t.createdAt)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <PersonAvatar name={t.clientName} tone="muted" />
                        <div className="min-w-0 truncate font-medium text-foreground">
                          {t.clientName}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <PersonAvatar name={t.therapistName} tone="primary" />
                        <div className="min-w-0">
                          <div className="truncate font-medium text-foreground leading-tight">
                            {t.therapistName}
                          </div>
                          {t.serviceName && (
                            <div className="mt-0.5 truncate text-xs text-muted-foreground">
                              {t.serviceName}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 text-right">
                      {breakdown.clientPaid == null ? (
                        <span className="text-muted-foreground" title="Cijena nije zabiljezena">
                          {"—"}
                        </span>
                      ) : (
                        <span
                          className="cursor-help underline decoration-border decoration-dotted underline-offset-4"
                          title={breakdownTitle(breakdown, shares)}
                        >
                          <span className="tabular-nums font-semibold text-foreground">
                            {formatMoney(shares.clientPays)}
                          </span>
                          <span className="ml-0.5 text-[11px] text-muted-foreground">KM</span>
                          {shares.cofunded && (
                            <span className="ml-1 text-[11px] text-muted-foreground">
                              + firma {formatMoney(shares.companyPays)}
                            </span>
                          )}
                        </span>
                      )}
                      {t.bookingCountry && (
                        <span
                          className="ml-1.5 text-xs text-muted-foreground"
                          title={t.bookingCountry}
                        >
                          {flagEmoji(t.bookingCountry)}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 text-right tabular-nums font-semibold text-foreground">
                      {formatMoney(breakdown.therapistEarnings)}
                      {breakdown.therapistEarnings !== null && (
                        <span className="ml-0.5 text-[11px] font-normal text-muted-foreground">KM</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <StatusBadge status={t.status} scheduledAt={t.scheduledAt} />
                    </td>
                    <td className="px-4 py-3.5">
                      {t.paymentStatus === "pending_transfer" ? (
                        <ConfirmPaymentButton sessionId={t.id} />
                      ) : (
                        <PaymentBadge status={t.paymentStatus} />
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <PayoutBadge payoutId={t.payoutId} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile/Tablet card layout */}
      <div className="grid gap-3 sm:grid-cols-2 lg:hidden">
        {therapies.map((t) => (
          <TherapyCard key={t.id} therapy={t} />
        ))}
      </div>

      {totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={(p) => onPageChange(p)}
        />
      )}
    </>
  );
}
